/**
 * SERVICE_TYPE_CONFIG — single source of truth for per-service-type booking behaviour.
 *
 * Any time a new service type is added:
 *  1. Add an entry here.
 *  2. BookingCalendar derives its selector, step manifest, and all copy from this config.
 *  3. BodyMapStep receives copy via props — no changes needed there.
 *
 * Mirrored in shared/serviceTypeConfig.ts for server-side use.
 */

export type ServiceTypeName = "tattoo" | "piercing";

/**
 * Step identifiers used in the booking funnel (in order).
 * A service type may opt out of any step by omitting it from `steps`.
 */
export type BookingStep =
  | "service-type"
  | "artist"
  | "calendar"
  | "time"
  | "estimate"
  | "body-map"
  | "images"
  | "notes"
  | "form";

export interface StepCopy {
  heading: string;
  subheading?: string;
  prompt?: string;
  sizeQuestion?: string;
  pinPromptOverlay?: string;
  toastNoPin?: string;
}

export interface ServiceTypeConfig {
  /** Unique slug — also used as the feature-flag key suffix */
  slug: ServiceTypeName;
  displayName: string;
  icon: string;
  description: string;
  /**
   * Feature flag key that gates this service type.
   * Empty string means always enabled (e.g. tattoo).
   */
  featureFlagKey: string;
  /**
   * Ordered list of booking steps for this service type.
   * `service-type` is always prepended at runtime when `withServiceTypeStep` is true.
   */
  steps: readonly BookingStep[];
  /** UI copy per step */
  stepCopy: {
    bodyMap: StepCopy;
    estimate?: StepCopy;
  };
  /** Service type options shown in the Details form dropdown */
  formServiceTypes: string[];
}

export const SERVICE_TYPE_CONFIG: Record<ServiceTypeName, ServiceTypeConfig> = {
  tattoo: {
    slug: "tattoo",
    displayName: "Tattoo",
    icon: "🖊️",
    description: "Custom, flash, cover-up & more",
    featureFlagKey: "",
    steps: ["artist", "calendar", "time", "estimate", "body-map", "images", "notes", "form"],
    stepCopy: {
      bodyMap: {
        heading: "Where does the tattoo go?",
        sizeQuestion: "How big is the tattoo?",
        prompt: "Tap or click to\nplace your tattoo\nlocation",
        toastNoPin: "Tap the body map above to mark your tattoo location first",
      },
      estimate: {
        heading: "Quick Price Estimate",
        subheading: "For personal planning only — not a quote from the studio.",
      },
    },
    formServiceTypes: ["Custom Tattoo", "Flash Design", "Cover-Up", "Touch-Up", "Consultation Only"],
  },
  piercing: {
    slug: "piercing",
    displayName: "Piercing",
    icon: "💎",
    description: "Ear, nose, body & more",
    featureFlagKey: "piercings_enabled",
    steps: ["artist", "calendar", "time", "estimate", "notes", "form"],
    stepCopy: {
      bodyMap: {
        heading: "Where is the piercing located?",
        sizeQuestion: "How large is the area?",
        prompt: "Tap or click to\nplace your piercing\nlocation",
        toastNoPin: "Tap the body map above to mark your piercing location first",
      },
      estimate: {
        heading: "Piercing Price Estimate",
        subheading: "Get a rough idea of cost before you book. Not a quote — your piercer will confirm pricing.",
      },
    },
    formServiceTypes: ["Piercing"],
  },
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Return config for the given service type, falling back to tattoo if unknown.
 */
export function getServiceTypeConfig(serviceType: ServiceTypeName | ""): ServiceTypeConfig {
  if (!serviceType || !SERVICE_TYPE_CONFIG[serviceType]) {
    return SERVICE_TYPE_CONFIG.tattoo;
  }
  return SERVICE_TYPE_CONFIG[serviceType];
}

/**
 * Returns true when the given step appears in this service type's step manifest.
 */
export function isStepVisible(serviceType: ServiceTypeName | "", step: BookingStep): boolean {
  const cfg = getServiceTypeConfig(serviceType);
  return cfg.steps.includes(step);
}

/**
 * Returns the step copy for the given step key.
 */
export function getStepCopy(serviceType: ServiceTypeName | "", step: keyof ServiceTypeConfig["stepCopy"]): StepCopy {
  const cfg = getServiceTypeConfig(serviceType);
  return cfg.stepCopy[step] ?? cfg.stepCopy.bodyMap;
}

/**
 * Derives the ordered main-step list for the booking funnel.
 *
 * @param serviceType - current selected service type (empty if not yet chosen)
 * @param withServiceTypeStep - true for public flows where the user picks a service
 * @param overrideSkipEstimate - true when estimate is bypassed for other reasons
 *        (flash booking, Variant D, URL estimate param)
 */
export function getMainSteps(
  serviceType: ServiceTypeName | "",
  withServiceTypeStep: boolean,
  overrideSkipEstimate: boolean,
): readonly BookingStep[] {
  const cfg = getServiceTypeConfig(serviceType);
  let steps: BookingStep[] = cfg.steps.filter((s) => {
    if (s === "estimate" && overrideSkipEstimate) return false;
    return true;
  }) as BookingStep[];

  if (withServiceTypeStep) {
    steps = ["service-type", ...steps];
  }

  return steps;
}

/**
 * Returns all service types that should be shown to the user given the current
 * feature-flag state. The tattoo type is always first.
 */
export function getAvailableServiceTypes(isFlagEnabled: (key: string) => boolean): ServiceTypeConfig[] {
  return Object.values(SERVICE_TYPE_CONFIG).filter((cfg) => {
    if (!cfg.featureFlagKey) return true;
    return isFlagEnabled(cfg.featureFlagKey);
  });
}
